import React, { useRef, useEffect, useMemo } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { XR } from "@react-three/xr";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { xrStore } from "../lib/xrstore";
import { computeSim, EVENTS } from "../lib/eclipse";
import { audio } from "../lib/audio";
import AerialScene from "./AerialScene";
import GroundScene from "./GroundScene";

const ORBIT = {
  wide: { pos: new THREE.Vector3(4, 5, 40), target: new THREE.Vector3(0, 0, 0) },
  narrow: { pos: new THREE.Vector3(4, 6, 50), target: new THREE.Vector3(0, 0, 0) },
};
const SOLAR = { pos: new THREE.Vector3(6, 16, 34), target: new THREE.Vector3(-8, 0, 0) };

// Autonomous orbital + temporal engine. Runs entirely inside the R3F frame loop
// so the Moon's orbital position advances continuously every frame, writing the
// live simulation into simRef and throttle-committing to React for the HUD.
function OrbitalClock({ simRef, clock }) {
  const lastCommit = useRef(0);
  const lastEvent = useRef(-1);
  useFrame((state, dt) => {
    const c = clock;
    const t = c.timeRef.current;
    const rate = c.playingRef.current ? (6 * c.speedRef.current) : 0;
    let nt = t + c.dirRef.current * dt * rate;
    if (nt >= 246) { nt = 246; c.dirRef.current = -1; }
    else if (nt <= 18) { nt = 18; c.dirRef.current = 1; }
    EVENTS.forEach((e, i) => {
      if (((t < e.min && nt >= e.min) || (t > e.min && nt <= e.min)) && i !== lastEvent.current) {
        lastEvent.current = i;
        audio.pulse(40 + i * 4, 0.8 + (i === 2 ? 0.5 : 0));
      }
    });
    c.timeRef.current = nt;
    simRef.current = computeSim(nt, c.danjonRef.current, c.bortleRef.current, c.b0Ref.current);
    if (state.clock.elapsedTime - lastCommit.current > 0.12) {
      lastCommit.current = state.clock.elapsedTime;
      c.onCommit(nt);
    }
  });
  return null;
}

function CameraRig({ perspective, controlsRef, orbitPreset, diveTarget }) {
  const { camera } = useThree();
  const desired = useRef(null);
  const dive = useMemo(() => (diveTarget ? new THREE.Vector3(...diveTarget) : null), [diveTarget]);
  useEffect(() => {
    desired.current = perspective === "solar" ? SOLAR : orbitPreset;
  }, [perspective, orbitPreset]);
  useFrame(() => {
    if (!controlsRef.current) return;
    // Cinematic dive: rush the camera down toward the clicked surface point.
    if (dive) {
      controlsRef.current.minDistance = 0.2;
      const target = dive.clone().multiplyScalar(1.16);
      camera.position.lerp(target, 0.14);
      controlsRef.current.target.lerp(dive, 0.14);
      controlsRef.current.update();
      return;
    }
    const d = desired.current;
    if (!d) return;
    camera.position.lerp(d.pos, 0.12);
    controlsRef.current.target.lerp(d.target, 0.12);
    controlsRef.current.update();
    if (camera.position.distanceTo(d.pos) < 0.08) desired.current = null;
  });
  return null;
}

function CanvasFrame({ view, simRef, bortle, withXR = false, perspective = "orbit", narrow = false, testid, primary = false, clock, onEarthClick, diveTarget = null }) {
  const controlsRef = useRef();
  const isAerial = view === "aerial";
  const orbitPreset = narrow ? ORBIT.narrow : ORBIT.wide;
  const camera = isAerial
    ? { position: orbitPreset.pos.toArray(), fov: narrow ? 55 : 52, near: 0.1, far: 400 }
    : { position: [14, 9, 42], fov: 62, near: 0.1, far: 500 };

  const content = (
    <>
      {isAerial ? (
        <AerialScene simRef={simRef} controlsRef={controlsRef} bortle={bortle} onEarthClick={onEarthClick} />
      ) : (
        <GroundScene simRef={simRef} bortle={bortle} />
      )}
      {primary && clock && <OrbitalClock simRef={simRef} clock={clock} />}
      {isAerial && <CameraRig perspective={perspective} controlsRef={controlsRef} orbitPreset={orbitPreset} diveTarget={diveTarget} />}
      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.08}
        enablePan={false}
        minDistance={isAerial ? 7 : 5}
        maxDistance={isAerial ? 160 : 110}
        target={isAerial ? orbitPreset.target.toArray() : [14, 7, -12]}
        maxPolarAngle={isAerial ? Math.PI : Math.PI * 0.72}
        rotateSpeed={0.6}
      />
    </>
  );

  return (
    <div className="canvas-host" data-testid={testid}>
      <Canvas
        dpr={[1, 2]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        camera={camera}
        shadows={false}
      >
        {withXR ? <XR store={xrStore}>{content}</XR> : content}
      </Canvas>
    </div>
  );
}

export default React.memo(CanvasFrame);
